import { tasksApi } from './tasksApi';

/**
 * Downscale + re-encode an image in the browser before upload — caps the
 * longest side and exports JPEG, so a multi-MB phone photo becomes a small
 * avatar. Falls back to the original file if anything goes wrong.
 */
export async function compressImage(
  file: File,
  maxDim = 512,
  quality = 0.82,
): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    // White backing so PNG transparency doesn't turn black in JPEG.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close();
    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', quality),
    );
    return blob && blob.size < file.size ? blob : file;
  } catch {
    return file; // unreadable/animated (e.g. GIF) — upload as-is
  }
}

/** Compress, presign, and PUT to S3. Returns the object's public URL. */
async function presignAndPut(endpoint: string, body: object, file: File): Promise<string> {
  const blob = await compressImage(file);
  const contentType = blob.type || file.type;
  const { uploadUrl, publicUrl } = await tasksApi<{ uploadUrl: string; publicUrl: string }>(
    endpoint,
    { method: 'POST', body: JSON.stringify({ ...body, contentType }) },
  );
  const put = await fetch(uploadUrl, {
    method: 'PUT',
    body: blob,
    headers: { 'Content-Type': contentType },
  });
  if (!put.ok) throw new Error(`Upload to storage failed (${put.status})`);
  return publicUrl;
}

/**
 * Upload a photo for a specific agent (admin/leader editing someone's profile).
 * Returns the public URL — the caller persists it (e.g. via provision).
 */
export function uploadAgentPhoto(externalId: string, file: File): Promise<string> {
  return presignAndPut('/v1/agents/photo-upload-url', { externalId }, file);
}

/**
 * Self-service: upload *your own* photo and persist it on your agent record.
 * Prefers the dedicated /me endpoints (which any provisioned agent may call).
 * Until those are deployed they 404 — in that case we fall back to the
 * leader/admin endpoints (presign by externalId + re-provision), which lets
 * leaders/admins update their own photo immediately. Returns the saved URL.
 */
export async function uploadMyPhoto(file: File): Promise<string> {
  try {
    const publicUrl = await presignAndPut('/v1/agents/me/photo-upload-url', {}, file);
    await tasksApi('/v1/agents/me/photo', {
      method: 'POST',
      body: JSON.stringify({ photoUrl: publicUrl }),
    });
    return publicUrl;
  } catch (err) {
    // Only fall back when the /me route isn't deployed yet (404); otherwise
    // surface the real error (bad file, storage failure, etc).
    if ((err as { status?: number }).status !== 404) throw err;

    const { agent } = await tasksApi<{
      agent: { externalId: string; name: string; email: string; team?: { key: string } | null } | null;
    }>('/v1/agents/me');
    if (!agent) throw new Error('You are not provisioned as an agent');
    if (!agent.team?.key) throw new Error('Your agent record has no team — ask an admin');

    // Admin presign is limited to TEAM_LEADER/ADMIN, so a plain agent gets a
    // clear message here until the /me endpoints are deployed.
    const publicUrl = await uploadAgentPhoto(agent.externalId, file);
    await tasksApi('/v1/agents', {
      method: 'POST',
      body: JSON.stringify({
        externalId: agent.externalId,
        name: agent.name,
        email: agent.email,
        teamKey: agent.team.key,
        photoUrl: publicUrl,
      }),
    });
    return publicUrl;
  }
}
